import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberInput,
    NumberInputField,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from "components-lib";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import ReactSelect from "react-select";
import { patientAdvices } from "./patientAdvices";
import { API_ENDPOINTS, axiosInstance } from "config/api";
import { humanizeApiError } from "../../../utils/api_error_samples";
import FileUploadField from "../../../components/FileUploadField";

const indianLanguages = [
    "Hindi",
    "Bengali",
    "Telugu",
    "Marathi",
    "Tamil",
    "Urdu",
    "Gujarati",
    "Kannada",
    "Malayalam",
    "Odia",
];

const DoctorForm = ({
    isOpen,
    onClose,
    handleAddDoctor,
    preselectedClinicId = null,
}) => {
    const toast = useToast();
    const [doctorData, setDoctorData] = useState({
        clinic_id: null,
        role: "",
        doctor: "",
        email: "",
        password: "",
        slot_duration: 0,
        opd_timing_weekdays: {
            Monday: [{ start: "11:00", end: "15:00" }],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
            Sunday: [],
        },
        emergency_charge: 0,
        outside_opd_charge: 0,
        tax_percentage: 10,
        services: [{ serviceName: "", unitPrice: "", discount: "" }],
        whatsapp_no: "",
        medical_license: "",
        qualification: "",
        payment_details: {
            account_holder: "",
            account_number: "",
            bank_name: "",
            ifsc_code: "",
            upi_id: "",
        },
        uploads: {
            doctor_signature: null,
            bar_code: null,
            qr_code: null,
            letterhead: null,
        },
        parameters: {
            height: "",
            weight: "",
            blood_pressure: "",
            spO2: "",
            pulse: "",
            temperature: "",
        },
        enableparameters: false,
        enableSpeechToText: false,
    });

    const [errors, setErrors] = useState({});
    const {
        isOpen: isErrorOpen,
        onOpen: onErrorOpen,
        onClose: onErrorClose,
    } = useDisclosure();
    const [errorMessage, setErrorMessage] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [selectedAdvices, setSelectedAdvices] = useState([]);
    const [doctorId, setDoctorId] = useState(32);
    const [clinicOptions, setClinicOptions] = useState([]);
    const [manualAdvice, setManualAdvice] = useState("");
    const [csvFile, setCsvFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showLetterheadPreview, setShowLetterheadPreview] = useState(false);
    const [advicesCsvFile, setAdvicesCsvFile] = useState(null);
    const [isAdvicesUploading, setIsAdvicesUploading] = useState(false);
    const [selectedPageSize, setSelectedPageSize] = useState("A4");
    const [letterheadPreviewUrl, setLetterheadPreviewUrl] = useState(null);
    const [letterheadHeight, setLetterheadHeight] = useState(0);
    const [isGeneralInfoSubmitted, setIsGeneralInfoSubmitted] = useState(false);
    const [isUploadsCompleted, setIsUploadsCompleted] = useState(false);

    // Dynamic advices state
    const [dynamicAdvices, setDynamicAdvices] = useState([]);
    const [advicesLoading, setAdvicesLoading] = useState(false);
    const letterheadImgRef = useRef(null);
    const [letterheadMargins, setLetterheadMargins] = useState({
        top: 100,
        right: 50,
        bottom: 50,
        left: 50,
    });

    // New states for doctor parameters
    const [doctorParameters, setDoctorParameters] = useState([]); // fetched from API
    const [selectedParameters, setSelectedParameters] = useState([]); // for dropdown selection

    useEffect(() => {
        if (isOpen) {
            axiosInstance
                .get("api/clinic/allClinics")
                .then((res) => {
                    let clinics = res.data;

                    // Handle different response structures
                    if (Array.isArray(clinics)) {
                        // If it's an array, process each item
                        clinics = clinics.map((clinic) => ({
                            id: clinic.id || clinic._id || clinic.clinic_id,
                            name: clinic.name || clinic.clinic_name,
                        }));
                    } else if (clinics && clinics.clinics) {
                        // If it has a clinics property, use that array
                        clinics = clinics.clinics.map((clinic) => ({
                            id: clinic.id || clinic._id || clinic.clinic_id,
                            name: clinic.name || clinic.clinic_name,
                        }));
                    } else if (clinics && typeof clinics === "object") {
                        // If it's a single object
                        clinics = [
                            {
                                id: clinics.id || clinics._id || clinics.clinic_id,
                                name: clinics.name || clinics.clinic_name,
                            },
                        ];
                    } else {
                        clinics = [];
                    }

                    setClinicOptions(clinics);

                    if (preselectedClinicId) {
                        setDoctorData((prev) => ({
                            ...prev,
                            clinic_id: preselectedClinicId,
                        }));
                    }
                })
                .catch((err) => {
                    console.error("Error fetching clinics:", err);
                    setErrorMessage("Failed to fetch clinics. Please try again.");
                    onErrorOpen();
                });
        }
    }, [isOpen]);

    // useEffect(() => {
    //     if (isOpen) {
    //         setDoctorData(prev => ({
    //             ...prev,
    //             clinic_id: preselectedClinicId || ""
    //         }));
    //     }
    // }, [isOpen, preselectedClinicId]);

    useEffect(() => {
        if (showLetterheadPreview) {
            const letterheadSettings = {
                pageSize: selectedPageSize,
                margins: letterheadMargins,
            };
        }
    }, [showLetterheadPreview, selectedPageSize, letterheadMargins]);

    // Handle CSV file selection for advices
    const handleAdvicesCsvFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setAdvicesCsvFile(file);
        }
    };

    // Handle CSV file upload for advices
    const handleAdvicesCsvUpload = async () => {
        if (!doctorId) return;

        setIsAdvicesUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", advicesCsvFile);
            formData.append("doctor_id", doctorId);
            // if (doctorData.clinic_id) formData.append('clinic_id', doctorData.clinic_id);

            const response = await axiosInstance.post(
                "/api/prescription/uploadAdvices",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    maxBodyLength: Infinity,
                }
            );

            console.log("Advices CSV upload response:", response.data);
            toast({
                title: "Success",
                description: "Advices CSV uploaded successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Refresh the advices list
            fetchAdvicesForDoctor(doctorId);
            setAdvicesCsvFile(null);
            // Clear the file input
            document.getElementById("advices-csv-file-input").value = "";
        } catch (error) {
            console.error("Error uploading advices CSV:", error);
            console.log("the error while uploaind csv was: ", error);
            toast({
                title: "Error",
                description: "Failed to upload advices CSV. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsAdvicesUploading(false);
        }
    };

    // Function to fetch advices based on clinic selection (moved before useEffect)
    // const fetchAdvicesForClinic = async (clinicId) => {
    //   if (!clinicId) {
    //     setDynamicAdvices([]);
    //     return;
    //   }

    //   setAdvicesLoading(true);
    //   try {
    //     const response = await axiosInstance.get(`/api/prescription/getAdvices?clinic_id=${clinicId}`);

    //     let advicesArray = [];
    //     if (response.data && response.data.data && Array.isArray(response.data.data)) {
    //       // Handle {data: [...]} structure
    //       advicesArray = response.data.data;
    //     } else if (response.data && response.data.advices) {
    //       advicesArray = response.data.advices;
    //     } else if (Array.isArray(response.data)) {
    //       advicesArray = response.data;
    //     } else {
    //       advicesArray = [];
    //     }

    //     // Transform the API response to match the expected format for ReactSelect
    //     const transformedAdvices = advicesArray.map(advice => ({
    //       value: advice.advice_text || advice.advice || advice.text || 'No advise text',
    //       label: advice.advice_text || advice.advice || advice.text || 'No advise text',
    //       id: advice.id
    //     }));

    //     setDynamicAdvices(transformedAdvices);
    //   } catch (error) {
    //     console.error('Error fetching advises:', error);
    //     setDynamicAdvices([]);
    //   } finally {
    //     setAdvicesLoading(false);
    //   }
    // };
    // Function to fetch advices based on clinic selection (moved before useEffect)
    const fetchAdvicesForDoctor = async (doctorId) => {
        if (!doctorId) {
            setDynamicAdvices([]);
            return;
        }

        setAdvicesLoading(true);
        try {
            const response = await axiosInstance.get(
                `/api/prescription/getAdvices?doctor_id=${doctorId}`
            );

            let advicesArray = [];
            if (
                response.data &&
                response.data.data &&
                Array.isArray(response.data.data)
            ) {
                // Handle {data: [...]} structure
                advicesArray = response.data.data;
            } else if (response.data && response.data.advices) {
                advicesArray = response.data.advices;
            } else if (Array.isArray(response.data)) {
                advicesArray = response.data;
            } else {
                advicesArray = [];
            }

            // Transform the API response to match the expected format for ReactSelect
            const transformedAdvices = advicesArray.map((advice) => ({
                value:
                    advice.advice_text ||
                    advice.advice ||
                    advice.text ||
                    "No advise text",
                label:
                    advice.advice_text ||
                    advice.advice ||
                    advice.text ||
                    "No advise text",
                id: advice.id,
            }));

            setDynamicAdvices(transformedAdvices);
        } catch (error) {
            console.error("Error fetching advises:", error);
            setDynamicAdvices([]);
        } finally {
            setAdvicesLoading(false);
        }
    };

    // Auto-fetch advices if clinic is already selected when component mounts
    // useEffect(() => {
    //   if (doctorData.clinic_id && dynamicAdvices.length === 0) {
    //     fetchAdvicesForClinic(doctorData.clinic_id);
    //   }
    // }, [doctorData.clinic_id, dynamicAdvices.length]);

    // Reset form state when modal opens
    useEffect(() => {
        if (isOpen) {
            resetFormState();
        }
    }, [isOpen]);

    // Reset form state function
    const resetFormState = () => {
        setDoctorId(null);
        setIsGeneralInfoSubmitted(false);
        setIsUploadsCompleted(false);
        setErrorMessage("");
        setFieldErrors({});
        setSelectedAdvices([]);
        setManualAdvice("");
        setDynamicAdvices([]);
        setDoctorData({
            clinic_id: "",
            role: "",
            doctor: "",
            email: "",
            password: "",
            slot_duration: 0,
            opd_timing_weekdays: {
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
                Saturday: [],
                Sunday: [],
            },
            emergency_charge: 0,
            outside_opd_charge: 0,
            services: [{ serviceName: "", unitPrice: "", discount: "" }],
            whatsapp_no: "",
            medical_license: "",
            qualification: "",
            payment_details: {
                account_holder: "",
                account_number: "",
                bank_name: "",
                ifsc_code: "",
                upi_id: "",
            },
            uploads: {
                doctor_signature: null,
                bar_code: null,
                qr_code: null,
                letterhead: null,
            },
            language: "",
            selectedWeekdays: [],
            timingStart: "",
            timingEnd: "",
            enableparameters: false,
            enableSpeechToText: false,
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // if (name === 'clinic_id') {
        //   // Fetch advices when clinic is selected
        //   if (value) {
        //     fetchAdvicesForClinic(value);
        //   } else {
        //     setDynamicAdvices([]);
        //   }
        // }
        setDoctorData({ ...doctorData, [name]: value });
    };

    const handleServiceChange = (index, field, value) => {
        const updatedServices = doctorData.services.map((service, i) =>
            i === index ? { ...service, [field]: value } : service
        );
        setDoctorData({ ...doctorData, services: updatedServices });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (name === "letterhead" && files[0]) {
            const fileUrl = URL.createObjectURL(files[0]);
            setLetterheadPreviewUrl(fileUrl);
            setShowLetterheadPreview(true);
        }
        setDoctorData({
            ...doctorData,
            uploads: { ...doctorData.uploads, [name]: files[0] },
        });
    };

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setDoctorData((prev) => ({
            ...prev,
            payment_details: {
                ...prev.payment_details,
                [name]: value,
            },
        }));
    };

    const handleClinicDetailsChange = (e) => {
        const { name, value } = e.target;
        setDoctorData((prev) => ({
            ...prev,
            clinic_details: {
                ...prev.clinic_details,
                [name]: value,
            },
        }));
    };

    const handleAddService = () => {
        setDoctorData({
            ...doctorData,
            services: [
                ...doctorData.services,
                { serviceName: "", unitPrice: "", discount: "" },
            ],
        });
    };

    const handleRemoveService = (index) => {
        const updatedServices = doctorData.services.filter((_, i) => i !== index);
        setDoctorData({ ...doctorData, services: updatedServices });
    };

    const handleparametersChange = (e) => {
        const { name, value } = e.target;
        setDoctorData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                [name]: value,
            },
        }));
    };

    const handleSpeechToggle = () => {
        setDoctorData((prev) => ({
            ...prev,
            enableSpeechToText: !prev.enableSpeechToText,
        }));
    };

    const handleCsvFileChange = (e) => {
        setCsvFile(e.target.files[0]);
    };

    const handleCsvUpload = async () => {
        if (!csvFile) {
            toast({
                title: "No file selected",
                description: "Please select a CSV file to upload",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!doctorData.clinic_id) {
            toast({
                title: "Clinic Required",
                description: "Please select a clinic first",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setIsUploading(true);

            // Create FormData object
            const formData = new FormData();
            formData.append("file", csvFile);
            formData.append("clinic_id", doctorData.clinic_id);

            // Upload CSV file
            const response = await axiosInstance.post(
                "/api/prescription/uploadAdvices",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            console.log("CSV upload response:", response.data);

            toast({
                title: "Upload Successful",
                description: "Patient advices have been uploaded successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Refresh the advice list to include the new advices
            // fetchAdvicesForClinic(doctorData.clinic_id);

            // Reset file input
            setCsvFile(null);
            const fileInput = document.getElementById("csv-file-input");
            if (fileInput) fileInput.value = "";
        } catch (error) {
            console.error("Error uploading CSV file:", error);
            toast({
                title: "Upload Failed",
                description:
                    error.response?.data?.message || "Failed to upload CSV file",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleParametersCsvUpload = async () => {
        if (!csvFile) {
            toast({
                title: "No file selected",
                description: "Please select a CSV file to upload",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!doctorData.clinic_id) {
            toast({
                title: "Clinic Required",
                description: "Please select a clinic first",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Check if doctor_id is available (should be set after general info submission)
        if (!doctorId) {
            toast({
                title: "Doctor ID Required",
                description: "Please submit general info first to generate a doctor ID",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setIsUploading(true);

            // Create FormData object with required parameters including doctor_id
            const formData = new FormData();
            formData.append("file", csvFile);
            formData.append("clinic_id", doctorData.clinic_id);
            formData.append("doctor_id", doctorId); // Add doctor_id to fix the 400 error

            // Upload CSV file
            const response = await axiosInstance.post(
                "/api/parameters/uploadParams",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            console.log("Parameters CSV upload response:", response.data);

            toast({
                title: "Upload Successful",
                description: "Patient parameters have been uploaded successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Reset file input
            setCsvFile(null);
            const fileInput = document.getElementById("parameters-csv-file-input");
            if (fileInput) fileInput.value = "";
        } catch (error) {
            console.error("Error uploading parameters CSV file:", error);
            toast({
                title: "Upload Failed",
                description:
                    error.response?.data?.message ||
                    "Failed to upload parameters CSV file",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    // const handleAddManualAdvice = async () => {
    //   if (!manualAdvice.trim()) return;

    //   if (!doctorData.clinic_id) {
    //     toast({
    //       title: 'Clinic Required',
    //       description: 'Please select a clinic first',
    //       status: 'warning',
    //       duration: 3000,
    //       isClosable: true,
    //     });
    //     return;
    //   }

    //   if (selectedAdvices.some(a => a.value === manualAdvice.trim())) {
    //     toast({
    //       title: 'Duplicate Advice',
    //       description: 'This advice already exists',
    //       status: 'warning',
    //       duration: 3000,
    //       isClosable: true,
    //     });
    //     return;
    //   }

    //   try {
    //     const response = await axiosInstance.post('/api/prescription/addAdvice', {
    //       advice_text: manualAdvice.trim(),
    //       clinic_id: doctorData.clinic_id,
    //       translations: {}
    //     });

    //     // Add to selected advices immediately
    //     setSelectedAdvices(prev => [...prev, {
    //       value: manualAdvice.trim(),
    //       label: manualAdvice.trim(),
    //       id: response.data.id || Date.now() // Use returned ID or fallback
    //     }]);

    //     // Refresh the advice list to include the new advice
    //     fetchAdvicesForClinic(doctorData.clinic_id);

    //     setManualAdvice('');

    //     toast({
    //       title: 'Success',
    //       description: 'Advice added successfully',
    //       status: 'success',
    //       duration: 3000,
    //       isClosable: true,
    //     });
    //   } catch (error) {
    //     console.error('Error adding advice:', error);
    //     toast({
    //       title: 'Error',
    //       description: 'Failed to add advice. Please try again.',
    //       status: 'error',
    //       duration: 3000,
    //       isClosable: true,
    //     });
    //   }
    // };

    const validateInputs = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/;
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

        if (!doctorData.clinic_id) newErrors.clinic_id = "Clinic is required.";
        if (!doctorData.role) newErrors.role = "Role is required.";
        if (!doctorData.doctor) newErrors.doctor = "Doctor name is required.";
        if (!doctorData.email) newErrors.email = "Email is required.";
        else if (!emailRegex.test(doctorData.email))
            newErrors.email = "Invalid email format.";
        if (!doctorData.password) newErrors.password = "Password is required.";
        if (!doctorData.medical_license)
            newErrors.medical_license = "Medical license is required.";
        if (!doctorData.qualification)
            newErrors.qualification = "Qualification is required.";
        if (!doctorData.whatsapp_no)
            newErrors.whatsapp_no = "WhatsApp number is required.";
        else if (!phoneRegex.test(doctorData.whatsapp_no))
            newErrors.whatsapp_no = "WhatsApp number must be 10 digits.";
        if (doctorData.services.length === 0)
            newErrors.services = "At least one service is required.";
        if (
            doctorData.payment_details.ifsc_code &&
            !ifscRegex.test(doctorData.payment_details.ifsc_code)
        )
            newErrors.ifsc_code = "Invalid IFSC code format.";
        if (
            doctorData.payment_details.account_holder &&
            !doctorData.payment_details.account_number
        )
            newErrors.account_number =
                "Account number is required when account holder is provided.";
        if (
            doctorData.payment_details.account_number &&
            !doctorData.payment_details.account_holder
        )
            newErrors.account_holder =
                "Account holder name is required when account number is provided.";
        if (
            doctorData.payment_details.account_number &&
            !doctorData.payment_details.bank_name
        )
            newErrors.bank_name =
                "Bank name is required when account number is provided.";
        const hasValidTimings = Object.values(doctorData.opd_timing_weekdays).some(
            (timings) => timings.length > 0
        );
        // Validate tax percentage
        const taxPercentVal = Number(doctorData.tax_percentage);
        if (
            Number.isNaN(taxPercentVal) ||
            taxPercentVal < 0 ||
            taxPercentVal > 100
        ) {
            newErrors.tax_percentage = "Tax % must be a number between 0 and 100.";
        }
        if (!hasValidTimings)
            newErrors.opd_timing = "At least one weekday must have OPD timings.";

        setErrors(newErrors);
        setFieldErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setErrorMessage(Object.values(newErrors)[0]);
            onErrorOpen();
            return false;
        }
        setErrorMessage("");
        return true;
    };

    // Function 1: Upload file to external API
    const uploadFile = async (file) => {
        const formData = new FormData();
        // const fileName = `uploaded-file-${Date.now()}.${file.name.split('.').pop()}`;
        formData.append("file", file);

        try {
            const uploadResponse = await axiosInstance.post("/api/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    // "Authorization": `Bearer ${token}`, // Ensure `token` is available in the scope
                },
                maxBodyLength: Infinity,
            });
            return uploadResponse?.data?.objectUrl;
        } catch (error) {
            console.error("File upload failed:", error);
            throw new Error("Failed to upload file.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("Submit button clicked - Starting form submission process");

        if (!validateInputs()) {
            console.log("Form validation failed");
            return;
        }
        console.log("Form validation passed");

        // Format the data according to the API requirements
        const formattedData = {
            doctor: doctorData.doctor,
            email: doctorData.email,
            password: doctorData.password,
            slot_duration: Number(doctorData.slot_duration),
            opd_timing: {
                monday:
                    doctorData.opd_timing_weekdays.Monday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Monday[0].start}-${doctorData.opd_timing_weekdays.Monday[0].end}`
                        : null,
                tuesday:
                    doctorData.opd_timing_weekdays.Tuesday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Tuesday[0].start}-${doctorData.opd_timing_weekdays.Tuesday[0].end}`
                        : null,
                wednesday:
                    doctorData.opd_timing_weekdays.Wednesday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Wednesday[0].start}-${doctorData.opd_timing_weekdays.Wednesday[0].end}`
                        : null,
                thursday:
                    doctorData.opd_timing_weekdays.Thursday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Thursday[0].start}-${doctorData.opd_timing_weekdays.Thursday[0].end}`
                        : null,
                friday:
                    doctorData.opd_timing_weekdays.Friday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Friday[0].start}-${doctorData.opd_timing_weekdays.Friday[0].end}`
                        : null,
                saturday:
                    doctorData.opd_timing_weekdays.Saturday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Saturday[0].start}-${doctorData.opd_timing_weekdays.Saturday[0].end}`
                        : null,
                sunday:
                    doctorData.opd_timing_weekdays.Sunday.length > 0
                        ? `${doctorData.opd_timing_weekdays.Sunday[0].start}-${doctorData.opd_timing_weekdays.Sunday[0].end}`
                        : null,
            },
            emergency_charge: Number(doctorData.emergency_charge),
            outside_opd_charge: Number(doctorData.outside_opd_charge),
            tax_percentage: Number(doctorData.tax_percentage),
            upi_details: doctorData.payment_details.upi_id,
            bank_details: `${doctorData.payment_details.bank_name}, A/C: ${doctorData.payment_details.account_number}, IFSC: ${doctorData.payment_details.ifsc_code}`,
            whatsapp_no: doctorData.whatsapp_no,
            medical_license: doctorData.medical_license,
            qualification: doctorData.qualification,
            services: doctorData.services.map((service) => ({
                serviceName: service.serviceName,
                unitPrice: Number(service.unitPrice),
                discount: Number(service.discount),
            })),
            clinic_id: Number(doctorData.clinic_id),
            role_in_clinic: doctorData.role,
            isParameters: doctorData.enableparameters ? 1 : 0,
            speech_to_text: doctorData.enableSpeechToText ? 1 : 0,
        };

        // Log the formatted data before API call
        console.log("=== DOCTOR REGISTRATION API CALL ===");
        console.log("API Endpoint:", API_ENDPOINTS.DOCTOR.REGISTER);
        console.log("Request Body:", JSON.stringify(formattedData, null, 2));

        try {
            // Make the API request
            console.log("Making API request to register doctor...");
            const response = await axiosInstance.post(
                API_ENDPOINTS.DOCTOR.REGISTER,
                formattedData
            );

            console.log("=== API RESPONSE ===");
            console.log("Status:", response.status);
            console.log("Response Data:", JSON.stringify(response.data, null, 2));

            if (response.data) {
                // Store doctor ID from backend response
                const doctorId =
                    response.data.id || response.data.doctor_id || response.data._id;
                console.log("Generated Doctor ID:", doctorId);
                setDoctorId(doctorId);
                setIsGeneralInfoSubmitted(true);

                // Add the new doctor to the list
                handleAddDoctor(response.data);

                // Show success message but don't close modal yet
                setErrorMessage(
                    "General info submitted successfully! Now you can upload files."
                );
                onErrorOpen();
            }
        } catch (error) {
            console.error("=== API ERROR ===", error);
            const serverMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message;
            const { message: humanMessage, field } = humanizeApiError(
                API_ENDPOINTS.DOCTOR.REGISTER,
                serverMessage
            );

            // Map known field keys from mapping to local form field names
            if (field) {
                // doctor form uses 'whatsapp_no' for phone field and 'medical_license' for license
                setFieldErrors({ [field]: humanMessage });
            } else {
                setFieldErrors({});
            }

            setErrorMessage(
                humanMessage || "Error registering doctor. Please try again."
            );
            onErrorOpen();
        }
    };

    const handleUploadSubmit = async () => {
        if (!doctorId) {
            setErrorMessage(
                "Please submit general info first to generate a doctor ID."
            );
            onErrorOpen();
            return;
        }

        try {
            const uploadKeys = [
                "doctor_signature",
                "bar_code",
                "qr_code",
                "letterhead",
            ];
            const filePromises = uploadKeys.map(async (key) => {
                const file = doctorData.uploads[key];
                if (!file) return null;
                const url = await uploadFile(file);
                return [key, url];
            });

            const fileResults = await Promise.allSettled(filePromises);

            const fileUrls = {};
            const errors = [];

            for (const res of fileResults) {
                if (!res || (res.status === "fulfilled" && res.value === null))
                    continue;
                if (res.status === "fulfilled") {
                    const [key, url] = res.value;
                    if (key && url) fileUrls[key] = url;
                } else {
                    // res.status === 'rejected'
                    errors.push(res.reason);
                }
            }

            // Now build payload using whatever URLs we have:
            const payload = {
                id: doctorId,
                letterhead: fileUrls.letterhead || null,
                doctor_signature: fileUrls.doctor_signature || null,
                qr_code: fileUrls.qr_code || null,
                bar_code: fileUrls.bar_code || null,
            };

            const uploadResponse = await axiosInstance.post(
                "api/doctor/uploadFiles",
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            setIsUploadsCompleted(true);

            // Inform user about partial failures if needed:
            if (errors.length) {
                toast({
                    title: "Partial Upload Failure",
                    description: `${errors.length} file(s) failed to upload. Other files were uploaded successfully.`,
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
            }

            setErrorMessage(
                "Uploads submitted successfully! Doctor registration completed."
            );
            onErrorOpen();

            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (error) {
            setErrorMessage("Error uploading files.");
            onErrorOpen();
        }
    };

    // Use dynamic advices if available, otherwise fallback to hardcoded ones
    const shouldUseDynamicAdvices =
        doctorData.clinic_id && dynamicAdvices && dynamicAdvices.length > 0;
    const adviceOptions = shouldUseDynamicAdvices
        ? dynamicAdvices
        : patientAdvices.map((a) => ({ value: a.value, label: a.label }));

    // Handler for toggles
    const handleToggle = (field) => {
        setDoctorData((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handlePageSizeChange = (value) => {
        setSelectedPageSize(value);
    };

    const handleClosePreview = () => {
        setShowLetterheadPreview(false);
        setLetterheadPreviewUrl(null);
    };

    // Sample prescription data
    const samplePrescription = {
        patientName: "John Doe",
        age: "35",
        gender: "Male",
        date: new Date().toLocaleDateString(),
        medicines: [
            {
                name: "Paracetamol",
                dosage: "500mg",
                frequency: "3 times daily",
                duration: "5 days",
            },
            {
                name: "Amoxicillin",
                dosage: "250mg",
                frequency: "2 times daily",
                duration: "7 days",
            },
            {
                name: "Cetirizine",
                dosage: "10mg",
                frequency: "Once daily",
                duration: "3 days",
            },
        ],
        diagnosis: "Upper Respiratory Tract Infection",
        notes:
            "Take medicines after meals. Complete the full course of antibiotics.",
    };

    // Fetch parameters when doctorId is set and general info is submitted
    useEffect(() => {
        if (doctorId && isGeneralInfoSubmitted) {
            const fetchDoctorParameters = async () => {
                try {
                    const response = await axiosInstance.get(
                        `/api/parameters/fetchByDoctorId?doctor_id=${doctorId}`
                    );
                    if (Array.isArray(response.data)) {
                        setDoctorParameters(response.data);
                    } else {
                        setDoctorParameters([]);
                    }
                } catch (error) {
                    setDoctorParameters([]);
                    console.error("Error fetching doctor parameters:", error);
                }
            };
            fetchDoctorParameters();
        } else {
            setDoctorParameters([]);
            setSelectedParameters([]);
        }
    }, [doctorId, isGeneralInfoSubmitted]);

    // State for Connect API UI
    const [isConnecting, setIsConnecting] = React.useState(false);
    const [connectResult, setConnectResult] = React.useState(null); // { success: boolean, message: string }

    // send connect request to API. expects doctorId to be available (doctorId variable used elsewhere in this file)
    async function sendConnectRequest() {
        if (!doctorId) {
            setConnectResult({
                success: false,
                message: "Doctor ID not available. Submit general info first.",
            });
            return;
        }

        // Use axiosInstance.get to call the connect endpoint with doctor_id as query param
        if (!doctorId) {
            setConnectResult({
                success: false,
                message: "Doctor ID not available. Submit general info first.",
            });
            return;
        }

        let data = JSON.stringify({
            newDoctorId: doctorId,
        });

        let config = {
            method: "post",
            maxBodyLength: Infinity,
            url: "/api/connectModule/connectDoctor",
            headers: {
                "Content-Type": "application/json",
            },
            data: data,
        };
        let response;

        async function makeRequest() {
            try {
                response = await axiosInstance.request(config);
                console.log(JSON.stringify(response.data));
            } catch (error) {
                console.log(error);
            }
        }

        try {
            setIsConnecting(true);
            setConnectResult(null);
            makeRequest();
            setConnectResult({
                success: true,
                message:
                    response ||
                    response?.message ||
                    response?.data?.message ||
                    response?.data?.error ||
                    response?.error ||
                    "Connected successfully",
            });
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || "Request failed";
            setConnectResult({ success: false, message: msg });
        } finally {
            setIsConnecting(false);
        }
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size="3xl"
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent maxW="800px">
                    <ModalHeader>Add Doctor</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {errorMessage && (
                            <Alert status="error" mb={4}>
                                <AlertIcon />
                                {errorMessage}
                            </Alert>
                        )}
                        <form onSubmit={handleSubmit}>
                            <Flex flexDirection="column" gap={4}>
                                {/* General Info Section */}
                                <Box
                                    opacity={isGeneralInfoSubmitted ? 0.6 : 1}
                                    pointerEvents={isGeneralInfoSubmitted ? "none" : "auto"}
                                    position="relative"
                                >
                                    {isGeneralInfoSubmitted && (
                                        <Box
                                            position="absolute"
                                            top="0"
                                            left="0"
                                            right="0"
                                            bottom="0"
                                            bg="rgba(255, 255, 255, 0.7)"
                                            zIndex="1"
                                            borderRadius="md"
                                        />
                                    )}
                                    <Text fontSize="lg" fontWeight="semibold" mb={4}>
                                        General Information
                                    </Text>
                                    <Flex flexDirection="column" gap={4}>
                                        <Flex gap={4} align="center" mb={2}>
                                            <FormControl
                                                isRequired
                                                isInvalid={!!fieldErrors.clinic_id}
                                                flex="1"
                                            >
                                                <FormLabel>Clinic</FormLabel>
                                                <Select
                                                    placeholder="Select Clinic"
                                                    name="clinic_id"
                                                    value={doctorData.clinic_id}
                                                    defaultValue={preselectedClinicId}
                                                    onChange={handleChange}
                                                >
                                                    {clinicOptions.map((clinic) => (
                                                        <option key={clinic.id} value={clinic.id}>
                                                            {clinic.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>
                                                    {fieldErrors.clinic_id}
                                                </FormErrorMessage>
                                            </FormControl>
                                            <FormControl
                                                isRequired
                                                isInvalid={!!fieldErrors.role}
                                                flex="1"
                                            >
                                                <FormLabel>Role in Clinic</FormLabel>
                                                <Flex gap={4}>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="role"
                                                            value="owner"
                                                            checked={doctorData.role === "owner"}
                                                            onChange={(e) =>
                                                                setDoctorData({
                                                                    ...doctorData,
                                                                    role: e.target.value,
                                                                })
                                                            }
                                                        />{" "}
                                                        Owner
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="role"
                                                            value="employee"
                                                            checked={doctorData.role === "employee"}
                                                            onChange={(e) =>
                                                                setDoctorData({
                                                                    ...doctorData,
                                                                    role: e.target.value,
                                                                })
                                                            }
                                                        />{" "}
                                                        Employee
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="role"
                                                            value="partner"
                                                            checked={doctorData.role === "partner"}
                                                            onChange={(e) =>
                                                                setDoctorData({
                                                                    ...doctorData,
                                                                    role: e.target.value,
                                                                })
                                                            }
                                                        />{" "}
                                                        Partner
                                                    </label>
                                                </Flex>
                                                <FormErrorMessage>{fieldErrors.role}</FormErrorMessage>
                                            </FormControl>
                                        </Flex>
                                        <Flex mb={2} gap={4} align="center">
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.doctor}>
                                                    <FormLabel>Doctor Name</FormLabel>
                                                    <Input
                                                        name="doctor"
                                                        value={doctorData.doctor}
                                                        onChange={handleChange}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.doctor}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.email}>
                                                    <FormLabel>Email</FormLabel>
                                                    <Input
                                                        name="email"
                                                        value={doctorData.email}
                                                        onChange={handleChange}
                                                        type="email"
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.email}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                        </Flex>
                                        <Flex gap={4}>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.password}>
                                                    <FormLabel>Password</FormLabel>
                                                    <Input
                                                        type="password"
                                                        name="password"
                                                        value={doctorData.password}
                                                        onChange={handleChange}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.password}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.slot_duration}>
                                                    <FormLabel>Slot Duration (minutes)</FormLabel>
                                                    <NumberInput value={doctorData.slot_duration}>
                                                        <NumberInputField
                                                            name="slot_duration"
                                                            onChange={handleChange}
                                                        />
                                                    </NumberInput>
                                                    <FormErrorMessage>
                                                        {fieldErrors.slot_duration}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                        </Flex>
                                        <Box>
                                            <Text fontSize="sm" fontWeight="semibold" mb={3}>
                                                Set OPD Timings for Weekdays
                                            </Text>
                                            <FormControl isInvalid={!!fieldErrors.opd_timing}>
                                                <FormLabel>Select Weekdays</FormLabel>
                                                <Flex wrap="wrap" gap={3} mb={2}>
                                                    {Object.keys(doctorData.opd_timing_weekdays).map(
                                                        (day) => (
                                                            <Box
                                                                key={day}
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                                width="120px"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    id={`weekday-checkbox-${day}`}
                                                                    checked={
                                                                        doctorData.selectedWeekdays?.includes(
                                                                            day
                                                                        ) || false
                                                                    }
                                                                    onChange={() => {
                                                                        setDoctorData((prev) => {
                                                                            const selected = prev.selectedWeekdays
                                                                                ? [...prev.selectedWeekdays]
                                                                                : [];
                                                                            const index = selected.indexOf(day);
                                                                            if (index > -1) {
                                                                                selected.splice(index, 1);
                                                                            } else {
                                                                                selected.push(day);
                                                                            }
                                                                            return {
                                                                                ...prev,
                                                                                selectedWeekdays: selected,
                                                                            };
                                                                        });
                                                                    }}
                                                                />
                                                                <label htmlFor={`weekday-checkbox-${day}`}>
                                                                    {day}
                                                                </label>
                                                            </Box>
                                                        )
                                                    )}
                                                </Flex>
                                                {(doctorData.selectedWeekdays || []).length > 0 && (
                                                    <Flex wrap="wrap" gap={2} mb={3}>
                                                        {doctorData.selectedWeekdays.map((day) => (
                                                            <Flex
                                                                key={day}
                                                                align="center"
                                                                bg="gray.200"
                                                                px={2}
                                                                py={1}
                                                                borderRadius="md"
                                                                gap={1}
                                                            >
                                                                <Text>{day}</Text>
                                                                <IconButton
                                                                    icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
                                                                    size="xs"
                                                                    colorScheme="red"
                                                                    aria-label={`Remove ${day}`}
                                                                    onClick={() => {
                                                                        setDoctorData((prev) => {
                                                                            const selected = prev.selectedWeekdays
                                                                                ? [...prev.selectedWeekdays]
                                                                                : [];
                                                                            const index = selected.indexOf(day);
                                                                            if (index > -1) {
                                                                                selected.splice(index, 1);
                                                                            }
                                                                            // Also clear timings for this day to mark as closed
                                                                            const newTimings = {
                                                                                ...prev.opd_timing_weekdays,
                                                                            };
                                                                            newTimings[day] = [];
                                                                            return {
                                                                                ...prev,
                                                                                selectedWeekdays: selected,
                                                                                opd_timing_weekdays: newTimings,
                                                                            };
                                                                        });
                                                                    }}
                                                                />
                                                            </Flex>
                                                        ))}
                                                    </Flex>
                                                )}
                                                <Flex gap={2} align="center" mb={3}>
                                                    <Input
                                                        type="time"
                                                        name="timingStart"
                                                        value={doctorData.timingStart || ""}
                                                        onChange={(e) =>
                                                            setDoctorData((prev) => ({
                                                                ...prev,
                                                                timingStart: e.target.value,
                                                            }))
                                                        }
                                                        placeholder="Start Time"
                                                        width="120px"
                                                    />
                                                    <Text>to</Text>
                                                    <Input
                                                        type="time"
                                                        name="timingEnd"
                                                        value={doctorData.timingEnd || ""}
                                                        onChange={(e) =>
                                                            setDoctorData((prev) => ({
                                                                ...prev,
                                                                timingEnd: e.target.value,
                                                            }))
                                                        }
                                                        placeholder="End Time"
                                                        width="120px"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        colorScheme="blue"
                                                        onClick={() => {
                                                            const {
                                                                timingStart,
                                                                timingEnd,
                                                                selectedWeekdays,
                                                                opd_timing_weekdays,
                                                            } = doctorData;
                                                            if (
                                                                !timingStart ||
                                                                !timingEnd ||
                                                                !selectedWeekdays ||
                                                                selectedWeekdays.length === 0
                                                            ) {
                                                                return;
                                                            }
                                                            const newTimings = { ...opd_timing_weekdays };
                                                            selectedWeekdays.forEach((day) => {
                                                                if (!newTimings[day]) newTimings[day] = [];
                                                                newTimings[day].push({
                                                                    start: timingStart,
                                                                    end: timingEnd,
                                                                });
                                                            });
                                                            setDoctorData((prev) => ({
                                                                ...prev,
                                                                opd_timing_weekdays: newTimings,
                                                                timingStart: "",
                                                                timingEnd: "",
                                                                selectedWeekdays: [],
                                                            }));
                                                        }}
                                                    >
                                                        Set Timing for Selected Days
                                                    </Button>
                                                </Flex>
                                                {Object.entries(doctorData.opd_timing_weekdays)
                                                    .filter(([day, timings]) => timings.length > 0)
                                                    .map(([day, timings]) => (
                                                        <Box
                                                            key={day}
                                                            mb={3}
                                                            borderWidth="1px"
                                                            borderColor="gray.300"
                                                            borderRadius="md"
                                                            p={3}
                                                        >
                                                            <Text fontWeight="bold" mb={2}>
                                                                {day}
                                                            </Text>
                                                            {timings.map((timeRange, idx) => (
                                                                <Flex key={idx} gap={2} align="center" mb={2}>
                                                                    <Text fontSize="sm">
                                                                        {timeRange.start} to {timeRange.end}
                                                                    </Text>
                                                                    <IconButton
                                                                        aria-label="Remove timing"
                                                                        icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
                                                                        size="sm"
                                                                        borderColor="transparent"
                                                                        colorScheme="red"
                                                                        onClick={() => {
                                                                            const newTimings = {
                                                                                ...doctorData.opd_timing_weekdays,
                                                                            };
                                                                            newTimings[day].splice(idx, 1);
                                                                            setDoctorData({
                                                                                ...doctorData,
                                                                                opd_timing_weekdays: newTimings,
                                                                            });
                                                                        }}
                                                                    />
                                                                </Flex>
                                                            ))}
                                                        </Box>
                                                    ))}
                                                <FormErrorMessage>
                                                    {fieldErrors.opd_timing}
                                                </FormErrorMessage>
                                            </FormControl>
                                        </Box>
                                        <Flex gap={4}>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.emergency_charge}>
                                                    <FormLabel>Emergency Charge</FormLabel>
                                                    <NumberInput value={doctorData.emergency_charge}>
                                                        <NumberInputField
                                                            name="emergency_charge"
                                                            onChange={handleChange}
                                                        />
                                                    </NumberInput>
                                                    <FormErrorMessage>
                                                        {fieldErrors.emergency_charge}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl
                                                    isInvalid={!!fieldErrors.outside_opd_charge}
                                                >
                                                    <FormLabel>Outside OPD Charge</FormLabel>
                                                    <NumberInput value={doctorData.outside_opd_charge}>
                                                        <NumberInputField
                                                            name="outside_opd_charge"
                                                            onChange={handleChange}
                                                        />
                                                    </NumberInput>
                                                    <FormErrorMessage>
                                                        {fieldErrors.outside_opd_charge}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.tax_percentage}>
                                                    <FormLabel>Tax %</FormLabel>
                                                    <NumberInput
                                                        value={doctorData.tax_percentage}
                                                        min={0}
                                                        max={100}
                                                    >
                                                        <NumberInputField
                                                            name="tax_percentage"
                                                            onChange={handleChange}
                                                        />
                                                    </NumberInput>
                                                    <FormErrorMessage>
                                                        {fieldErrors.tax_percentage}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.whatsapp_no}>
                                                    <FormLabel>WhatsApp Number</FormLabel>
                                                    <Input
                                                        name="whatsapp_no"
                                                        value={doctorData.whatsapp_no}
                                                        onChange={handleChange}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.whatsapp_no}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                        </Flex>
                                        <Flex gap={4}>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.medical_license}>
                                                    <FormLabel>Medical License</FormLabel>
                                                    <Input
                                                        name="medical_license"
                                                        value={doctorData.medical_license}
                                                        onChange={handleChange}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.medical_license}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                            <Box flex="1">
                                                <FormControl isInvalid={!!fieldErrors.qualification}>
                                                    <FormLabel>Qualification</FormLabel>
                                                    <Input
                                                        name="qualification"
                                                        value={doctorData.qualification}
                                                        onChange={handleChange}
                                                    />
                                                    <FormErrorMessage>
                                                        {fieldErrors.qualification}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </Box>
                                        </Flex>
                                        <FormControl isInvalid={!!fieldErrors.services}>
                                            <FormLabel mt={4}>Services</FormLabel>
                                            {doctorData.services.map((service, idx) => (
                                                <Flex key={idx} gap={3} mb={2} align="center">
                                                    <Input
                                                        placeholder="Service Name"
                                                        value={service.serviceName}
                                                        onChange={(e) =>
                                                            handleServiceChange(
                                                                idx,
                                                                "serviceName",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="Unit Price"
                                                        value={service.unitPrice}
                                                        onChange={(e) =>
                                                            handleServiceChange(
                                                                idx,
                                                                "unitPrice",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="Discount %"
                                                        value={service.discount}
                                                        onChange={(e) =>
                                                            handleServiceChange(
                                                                idx,
                                                                "discount",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <IconButton
                                                        icon={<div style={{ fontSize: "1rem", color: "white"}}>X</div>}
                                                        onClick={() => handleRemoveService(idx)}
                                                        border="none"
                                                        colorScheme="red"
                                                        aria-label="Remove Service"
                                                        size="sm"
                                                    />
                                                </Flex>
                                            ))}
                                            <FormErrorMessage>
                                                {fieldErrors.services}
                                            </FormErrorMessage>
                                        </FormControl>
                                        <Button size="sm" onClick={handleAddService}>
                                            + Add Service
                                        </Button>
                                    </Flex>

                                    <Divider my={6} />
                                    <Text fontSize="lg"   fontWeight="semibold">
                                        Payment Details
                                    </Text>
                                    <Flex gap={2} mb={2}>
                                        <Box flex="1">
                                            <FormControl>
                                                <FormLabel>Account Holder Name</FormLabel>
                                                <Input
                                                    name="account_holder"
                                                    value={doctorData.payment_details.account_holder}
                                                    onChange={handlePaymentChange}
                                                />
                                            </FormControl>
                                        </Box>
                                        <Box flex="1">
                                            <FormControl>
                                                <FormLabel>Account Number</FormLabel>
                                                <Input
                                                    name="account_number"
                                                    value={doctorData.payment_details.account_number}
                                                    onChange={handlePaymentChange}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Flex>
                                    <Flex gap={2} mb={2}>
                                        <Box flex="1">
                                            <FormControl>
                                                <FormLabel>Bank Name</FormLabel>
                                                <Input
                                                    name="bank_name"
                                                    value={doctorData.payment_details.bank_name}
                                                    onChange={handlePaymentChange}
                                                />
                                            </FormControl>
                                        </Box>
                                        <Box flex="1">
                                            <FormControl>
                                                <FormLabel>IFSC Code</FormLabel>
                                                <Input
                                                    name="ifsc_code"
                                                    value={doctorData.payment_details.ifsc_code}
                                                    onChange={handlePaymentChange}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Flex>
                                    <Flex gap={2} mb={2}>
                                        <Box flex="1">
                                            <FormControl>
                                                <FormLabel>UPI ID</FormLabel>
                                                <Input
                                                    name="upi_id"
                                                    value={doctorData.payment_details.upi_id}
                                                    onChange={handlePaymentChange}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Flex>
                                    <Divider my={6} />
                                    <Box mb={4}>
                                        <FormLabel>Parameters for Patient</FormLabel>
                                        {/* Parameters and Speech to Text toggles */}
                                        <Box
                                            mb={4}
                                            p={4}
                                            borderWidth={1}
                                            borderColor="rgb(226, 232, 240)"
                                            borderRadius="md"
                                        >
                                            <Flex align="center" gap={6}>
                                                <Flex align="center" gap={2}>
                                                    <Text fontWeight="medium">parameters:</Text>
                                                    <Button
                                                        size="sm"
                                                        colorScheme={
                                                            doctorData.enableparameters ? "green" : "gray"
                                                        }
                                                        onClick={() => handleToggle("enableparameters")}
                                                    >
                                                        {doctorData.enableparameters
                                                            ? "Enabled"
                                                            : "Disabled"}
                                                    </Button>
                                                </Flex>
                                                <Flex align="center" gap={2}>
                                                    <Text fontWeight="medium">Speech to Text:</Text>
                                                    <Button
                                                        size="sm"
                                                        colorScheme={
                                                            doctorData.enableSpeechToText ? "green" : "gray"
                                                        }
                                                        onClick={() => handleToggle("enableSpeechToText")}
                                                    >
                                                        {doctorData.enableSpeechToText
                                                            ? "Enabled"
                                                            : "Disabled"}
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        </Box>
                                        {/* Parameters section has been moved up to the Advises for Patient section */}
                                    </Box>
                                </Box>

                                <Button
                                    // colorScheme={isGeneralInfoSubmitted ? "green" : "blue.400"}
                                    _hover={{ bg: "blue.600" }}
                                    mt={2}
                                    bg={"blue.500"}
                                    type="submit"
                                    isDisabled={isGeneralInfoSubmitted}
                                >
                                    {isGeneralInfoSubmitted
                                        ? "✓ General Info Submitted"
                                        : "Submit General Info"}
                                </Button>

                                {isGeneralInfoSubmitted && (
                                    <Alert status="success" mt={4}>
                                        <AlertIcon />
                                        General information submitted successfully! Doctor ID:{" "}
                                        {doctorId}
                                    </Alert>
                                )}
                                {/* Connect to external module UI - single Connect button */}
                                <Box
                                    mt={4}
                                    mb={2}
                                    p={3}
                                    borderWidth="1px"
                                    borderColor="rgb(226, 232, 240)"
                                    borderRadius="md"
                                >
                                    <Text fontSize="md" fontWeight="semibold" mb={2}>
                                        Connect Module
                                    </Text>
                                    <FormControl mb={2}>
                                        <Flex gap={4} align="center">
                                            <Button
                                                size="sm"
                                                colorScheme="blue"
                                                onClick={sendConnectRequest}
                                                isLoading={isConnecting}
                                                isDisabled={!doctorId}
                                            >
                                                {isConnecting ? "Connecting..." : "Connect to Module"}
                                            </Button>
                                            {!doctorId && (
                                                <Text fontSize="xs" color="gray.600">
                                                    Doctor ID not generated yet. Submit general info to
                                                    enable.
                                                </Text>
                                            )}
                                        </Flex>
                                        {connectResult && (
                                            <Alert
                                                mt={3}
                                                status={connectResult.success ? "success" : "error"}
                                            >
                                                <AlertIcon />
                                                {connectResult.message}
                                            </Alert>
                                        )}
                                    </FormControl>
                                </Box>
                                <Divider my={6} />
                                <Text fontSize="lg" fontWeight="semibold">
                                    Consultation
                                </Text>
                                <FormControl>
                                    {/* CSV Upload Section for Parameters */}
                                    <Box mb={4}>
                                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                                            Upload Parameters CSV File
                                        </Text>
                                        <Flex gap={2} align="center">
                                            <FileUploadField
                                                id="csv-file-input"
                                                name="parameters_csv"
                                                onChange={handleCsvFileChange}
                                                accept=".csv"
                                                isDisabled={!doctorData.clinic_id}
                                                helperText="CSV with parameter list"
                                            />
                                            <Button
                                                onClick={handleParametersCsvUpload}
                                                colorScheme="blue"
                                                isLoading={isUploading}
                                                isDisabled={!csvFile || !doctorData.clinic_id}
                                            >
                                                Upload
                                            </Button>
                                        </Flex>
                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                            Upload a CSV file containing patient parameters. The file
                                            should have parameters text in each row. To update the
                                            parameters list please only include the new parameters
                                            list in the csv file.
                                        </Text>
                                    </Box>
                                    {/* Parameters selection (dynamic) */}
                                    <Flex gap={2} align="center">
                                        <Box flex="2">
                                            <ReactSelect
                                                isMulti
                                                name="parameters_dynamic"
                                                options={doctorParameters.map((param) => ({
                                                    value: param.name,
                                                    label: param.unit
                                                        ? `${param.name} (${param.unit})`
                                                        : param.name,
                                                    id: param.id,
                                                    unit: param.unit,
                                                }))}
                                                value={selectedParameters}
                                                onChange={(selected) => {
                                                    setSelectedParameters(selected);
                                                    setDoctorData((prev) => ({
                                                        ...prev,
                                                        parameters: selected.reduce((acc, curr) => {
                                                            acc[curr.value] =
                                                                prev.parameters?.[curr.value] || "";
                                                            return acc;
                                                        }, {}),
                                                    }));
                                                }}
                                                placeholder={
                                                    doctorId
                                                        ? doctorParameters.length > 0
                                                            ? "Select parameters..."
                                                            : "No parameters found"
                                                        : "No doctor ID"
                                                }
                                                isDisabled={!doctorId}
                                            />
                                        </Box>
                                    </Flex>
                                    {doctorParameters.length > 0 && (
                                        <Text fontSize="xs" color="green.600" mt={1}>
                                            Loaded {doctorParameters.length} parameters for this
                                            doctor
                                        </Text>
                                    )}
                                    {doctorId && (
                                        <Button
                                            size="xs"
                                            onClick={async () => {
                                                try {
                                                    const response = await axiosInstance.get(
                                                        `/api/parameters/fetchByDoctorId?doctor_id=${doctorId}`
                                                    );
                                                    if (Array.isArray(response.data)) {
                                                        setDoctorParameters(response.data);
                                                        toast({
                                                            title: "Parameters Refreshed",
                                                            description: "Parameter list has been updated",
                                                            status: "success",
                                                            duration: 2000,
                                                            isClosable: true,
                                                        });
                                                    } else {
                                                        setDoctorParameters([]);
                                                        toast({
                                                            title: "Error",
                                                            description: "No parameters found",
                                                            status: "error",
                                                            duration: 3000,
                                                            isClosable: true,
                                                        });
                                                    }
                                                } catch (error) {
                                                    setDoctorParameters([]);
                                                    toast({
                                                        title: "Error",
                                                        description: "Failed to refresh parameters",
                                                        status: "error",
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            colorScheme="blue"
                                            variant="outline"
                                            mt={2}
                                        >
                                            Refresh Parameters
                                        </Button>
                                    )}
                                    {/* Render input fields for each selected parameter (dynamic) */}
                                    {selectedParameters.map((param) => (
                                        <Box key={param.id} mb={2}>
                                            <FormLabel fontSize="sm">{param.label}</FormLabel>
                                            <Input
                                                name={param.value}
                                                value={doctorData.parameters?.[param.value] || ""}
                                                onChange={(e) => {
                                                    const { name, value } = e.target;
                                                    setDoctorData((prev) => ({
                                                        ...prev,
                                                        parameters: {
                                                            ...prev.parameters,
                                                            [name]: value,
                                                        },
                                                    }));
                                                }}
                                                placeholder={`Enter value${param.unit ? ` (${param.unit})` : ""
                                                    }`}
                                            />
                                        </Box>
                                    ))}
                                    <Divider my={6} />
                                    <Text fontSize="lg" fontWeight="semibold">
                                        Advices for Patient
                                    </Text>
                                    <FormControl>
                                        <FormLabel>Advices</FormLabel>
                                        <Box mb={4}>
                                            <Text fontSize="sm" fontWeight="medium" mb={2}>
                                                Upload Advices CSV File
                                            </Text>
                                            <Flex gap={2} align="center">
                                                <Input
                                                    id="advices-csv-file-input"
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={handleAdvicesCsvFileChange}
                                                    size="md"
                                                    width="300px"
                                                    isDisabled={!doctorData.clinic_id}
                                                />
                                                <Button
                                                    onClick={handleAdvicesCsvUpload}
                                                    colorScheme="blue"
                                                    isLoading={isAdvicesUploading}
                                                    isDisabled={!advicesCsvFile || !doctorData.clinic_id}
                                                >
                                                    Upload
                                                </Button>
                                            </Flex>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Upload a CSV file containing patient advices. The file
                                                should have advices text in each row.
                                            </Text>
                                        </Box>
                                        <ReactSelect
                                            isMulti
                                            name="advices"
                                            options={adviceOptions}
                                            value={selectedAdvices}
                                            onChange={setSelectedAdvices}
                                            placeholder="Select advices..."
                                            isDisabled={!doctorData.clinic_id}
                                        />
                                        {adviceOptions.length > 0 && (
                                            <Text fontSize="xs" color="green.600" mt={1}>
                                                Loaded {adviceOptions.length} advices for the Doctor
                                            </Text>
                                        )}
                                        {doctorData.clinic_id && (
                                            <Button
                                                size="xs"
                                                onClick={async () => {
                                                    try {
                                                        await fetchAdvicesForDoctor(doctorId);
                                                        toast({
                                                            title: "Advices Refreshed",
                                                            description: "Advice list has been updated",
                                                            status: "success",
                                                            duration: 2000,
                                                            isClosable: true,
                                                        });
                                                    } catch (error) {
                                                        toast({
                                                            title: "Error",
                                                            description: "Failed to refresh advices",
                                                            status: "error",
                                                            duration: 3000,
                                                            isClosable: true,
                                                        });
                                                    }
                                                }}
                                                colorScheme="blue"
                                                variant="outline"
                                                mt={2}
                                            >
                                                Refresh Advices
                                            </Button>
                                        )}
                                    </FormControl>
                                </FormControl>
                                <Divider my={6} />
                                <Text fontSize="lg" fontWeight="semibold">
                                    File Uploads
                                </Text>

                                {!isGeneralInfoSubmitted && (
                                    <Alert status="info" mb={4}>
                                        <AlertIcon />
                                        Please submit general info first to enable file uploads.
                                    </Alert>
                                )}

                                {["doctor_signature", "bar_code", "qr_code", "letterhead"].map(
                                    (key) => (
                                        <FileUploadField
                                            key={key}
                                            label={key.replace("_", " ")}
                                            id={`file-${key}`}
                                            name={key}
                                            onChange={handleFileChange}
                                            isDisabled={!isGeneralInfoSubmitted}
                                            accept={
                                                key === "letterhead"
                                                    ? "image/*,application/pdf"
                                                    : "image/*"
                                            }
                                        />
                                    )
                                )}
                                <Flex gap={3}>
                                    <Button
                                        colorScheme={isUploadsCompleted ? "blue" : "blue"}
                                        onClick={handleUploadSubmit}
                                        isDisabled={!isGeneralInfoSubmitted || isUploadsCompleted}
                                        flex="1"
                                    >
                                        {isUploadsCompleted
                                            ? "✓ Uploads Completed"
                                            : "Submit Uploads"}
                                    </Button>

                                    {isGeneralInfoSubmitted && !isUploadsCompleted && (
                                        <Button
                                            colorScheme="gray"
                                            variant="outline"
                                            onClick={() => {
                                                setIsUploadsCompleted(true);
                                                setErrorMessage(
                                                    "Doctor registration completed without uploads."
                                                );
                                                onErrorOpen();
                                                setTimeout(() => {
                                                    onClose();
                                                }, 2000);
                                            }}
                                        >
                                            Skip Uploads
                                        </Button>
                                    )}
                                </Flex>

                                {isUploadsCompleted && (
                                    <Alert status="success" mt={4}>
                                        <AlertIcon />
                                        All uploads completed successfully! Doctor registration is
                                        complete.
                                    </Alert>
                                )}
                            </Flex>
                        </form>
                    </ModalBody>
                    <ModalFooter />
                </ModalContent>
            </Modal>

            {/* Letterhead Preview Modal */}
            <Modal
                isOpen={showLetterheadPreview}
                onClose={handleClosePreview}
                size="xl"
            >
                <ModalOverlay />
                <ModalContent maxW="1000px" width="1000px">
                    <ModalHeader>Letterhead Preview</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box mb={4}>
                            <FormControl>
                                <FormLabel>Select Page Size</FormLabel>
                                <RadioGroup
                                    value={selectedPageSize}
                                    onChange={handlePageSizeChange}
                                >
                                    <Stack direction="row">
                                        <Radio value="A4">A4</Radio>
                                        <Radio value="A5">A5</Radio>
                                    </Stack>
                                </RadioGroup>
                            </FormControl>
                            <FormControl mt={4} maxW="400px">
                                <FormLabel>Content Margins (px)</FormLabel>
                                <Text fontSize="xs" color="gray.600" mb={2}>
                                    Adjust the positioning of prescription content on the
                                    letterhead
                                </Text>
                                <Flex gap={2} wrap="wrap">
                                    <Box>
                                        <Text fontSize="sm">Top</Text>
                                        <NumberInput
                                            min={0}
                                            max={200}
                                            value={letterheadMargins.top}
                                            onChange={(val) =>
                                                setLetterheadMargins((m) => ({
                                                    ...m,
                                                    top: Number(val),
                                                }))
                                            }
                                        >
                                            <NumberInputField width="60px" />
                                        </NumberInput>
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm">Right</Text>
                                        <NumberInput
                                            min={0}
                                            max={200}
                                            value={letterheadMargins.right}
                                            onChange={(val) =>
                                                setLetterheadMargins((m) => ({
                                                    ...m,
                                                    right: Number(val),
                                                }))
                                            }
                                        >
                                            <NumberInputField width="60px" />
                                        </NumberInput>
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm">Bottom</Text>
                                        <NumberInput
                                            min={0}
                                            max={200}
                                            value={letterheadMargins.bottom}
                                            onChange={(val) =>
                                                setLetterheadMargins((m) => ({
                                                    ...m,
                                                    bottom: Number(val),
                                                }))
                                            }
                                        >
                                            <NumberInputField width="60px" />
                                        </NumberInput>
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm">Left</Text>
                                        <NumberInput
                                            min={0}
                                            max={200}
                                            value={letterheadMargins.left}
                                            onChange={(val) =>
                                                setLetterheadMargins((m) => ({
                                                    ...m,
                                                    left: Number(val),
                                                }))
                                            }
                                        >
                                            <NumberInputField width="60px" />
                                        </NumberInput>
                                    </Box>
                                </Flex>
                            </FormControl>
                        </Box>
                        <Box
                            borderWidth="1px"
                            borderRadius="md"
                            bg="white"
                            width={selectedPageSize === "A4" ? "210mm" : "148mm"}
                            height={selectedPageSize === "A4" ? "297mm" : "210mm"}
                            margin="0 auto"
                            position="relative"
                            overflow="hidden"
                            backgroundImage={
                                letterheadPreviewUrl ? `url(${letterheadPreviewUrl})` : "none"
                            }
                            backgroundSize="cover"
                            backgroundPosition="center"
                            backgroundRepeat="no-repeat"
                        >
                            {/* Hidden image for calculating dimensions */}
                            {letterheadPreviewUrl && (
                                <img
                                    src={letterheadPreviewUrl}
                                    alt="Letterhead"
                                    ref={letterheadImgRef}
                                    style={{ display: "none" }}
                                    onLoad={(e) => {
                                        if (letterheadImgRef.current) {
                                            setLetterheadHeight(
                                                letterheadImgRef.current.naturalHeight *
                                                (letterheadImgRef.current.width /
                                                    letterheadImgRef.current.naturalWidth)
                                            );
                                        }
                                    }}
                                />
                            )}
                            {/* Content container positioned in the center with user-defined margins */}
                            <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                width="100%"
                                height="100%"
                                position="absolute"
                                top="0"
                                left="0"
                                zIndex={2}
                                p={`${letterheadMargins.top}px ${letterheadMargins.right}px ${letterheadMargins.bottom}px ${letterheadMargins.left}px`}
                            >
                                {/* Content area with semi-transparent background for better readability */}
                                <Box
                                    width="100%"
                                    maxW="600px"
                                    mx="auto"
                                    bg="rgba(255, 255, 255, 0.9)"
                                    borderRadius="md"
                                    p={6}
                                    boxShadow="sm"
                                >
                                    {/* Patient Info and Date */}
                                    <Flex
                                        justify="space-between"
                                        align="flex-start"
                                        width="100%"
                                        mb={6}
                                    >
                                        <Box>
                                            <Text fontWeight="bold" fontSize="md">
                                                Patient Name: {samplePrescription.patientName}
                                            </Text>
                                            <Text fontSize="sm">Age: {samplePrescription.age}</Text>
                                            <Text fontSize="sm">
                                                Gender: {samplePrescription.gender}
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium">
                                                Date: {samplePrescription.date}
                                            </Text>
                                        </Box>
                                    </Flex>

                                    {/* Diagnosis */}
                                    <Box mb={4}>
                                        <Text fontWeight="bold" mb={2} fontSize="md">
                                            Diagnosis:
                                        </Text>
                                        <Text fontSize="sm">{samplePrescription.diagnosis}</Text>
                                    </Box>

                                    {/* Medicines */}
                                    <Box mb={4}>
                                        <Text fontWeight="bold" mb={2} fontSize="md">
                                            Medicines:
                                        </Text>
                                        {samplePrescription.medicines.map((medicine, index) => (
                                            <Flex key={index} mb={2} fontSize="sm">
                                                <Text width="30%" fontWeight="medium">
                                                    {medicine.name}
                                                </Text>
                                                <Text width="20%">{medicine.dosage}</Text>
                                                <Text width="25%">{medicine.frequency}</Text>
                                                <Text width="25%">{medicine.duration}</Text>
                                            </Flex>
                                        ))}
                                    </Box>

                                    {/* Notes */}
                                    <Box mb={4}>
                                        <Text fontWeight="bold" mb={2} fontSize="md">
                                            Notes:
                                        </Text>
                                        <Text fontSize="sm">{samplePrescription.notes}</Text>
                                    </Box>

                                    {/* Doctor's Signature */}
                                    <Box mt={8}>
                                        <Text fontWeight="bold" fontSize="md">
                                            Doctor's Signature
                                        </Text>
                                        <Box
                                            height="50px"
                                            borderBottom="1px"
                                            borderStyle="dashed"
                                            borderColor="gray.400"
                                        />
                                    </Box>
                                </Box>
                            </Flex>
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleClosePreview}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default DoctorForm;
